import type { ProjectResponse, UserOption } from '@proerp/shared';
import { useUserOptions } from '@/lib/useUserOptions';

type LeadFields = Pick<ProjectResponse, 'projectLeadUserIds' | 'projectManager'>;

/**
 * Anzeige der Projektleitung (Meldung 2026-10-06). Maßgeblich ist die Auswahl
 * unter Projektdaten (`projectLeadUserIds`, echte Benutzer). Das Textfeld
 * `projectManager` stammt aus dem Altdaten-Import und gilt nur, solange niemand
 * gewählt ist — die Übersicht zeigte es bis dahin immer, sodass eine neue Wahl
 * dort nie ankam.
 *
 * Solange die Benutzerliste lädt: `null` statt des alten Werts — sonst stünde
 * kurz „MP" da, wo gleich „AG" kommt.
 */
export function projectLeadLabel(project: LeadFields, users: readonly UserOption[] | undefined): string | null {
  if (project.projectLeadUserIds.length === 0) return project.projectManager;
  if (!users) return null;
  const names = project.projectLeadUserIds
    .map((id) => users.find((user) => user.id === id))
    .filter((user): user is UserOption => Boolean(user))
    .map((user) => user.initials ?? user.name);
  return names.length > 0 ? names.join(', ') : project.projectManager;
}

/** Wie `projectLeadLabel`, lädt die Benutzer selbst; `undefined` solange das Projekt lädt. */
export function useProjectLeadLabel(project: LeadFields | undefined): string | null {
  const users = useUserOptions().data?.data;
  return project ? projectLeadLabel(project, users) : null;
}
